import React, { useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { Currency } from 'chaika-enums';

import style from './SubscriptionCreationPopup.module.scss';

import { Optional } from 'chaika-frontend-types/frontend-cms';

import { translate } from '@chaika/i18n/helper';
import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { AsyncUserSelect } from '@components/ModelSelect/AsyncUserSelect';
import { PopupModifiers } from '@components/Popup';
import { Preloader } from '@components/Preloader';
import { PreloaderModifiers } from '@components/Preloader/Preloader';
import { RecurringSubscriptionTermsEditor } from '@common/RecurringSubscriptionTermsEditor';
import { useRecurringSubscriptionTerms } from '@common/Chat/hooks/useRecurringSubscriptionTerms';
import { useSubscriptionBillingAmount } from '@common/Chat/hooks/useSubscriptionBillingAmount';
import { useEhrSubscription } from '@modules/patient/hooks/useEhrSubscription';
import { store } from '@store';
import { Money } from '@store/models/Money';
import { recurringSubscriptionTrialDurationQueryOptions } from '@store/RecurringSubscription/queries/recurringSubscriptionTrialDurationQueries';
import { userPracticeQueryOptions } from '@user/queries/userPracticeQueries';
import { Declension } from '@utils/Declension';
import { assertIsDefined } from '@utils/assertIsDefined';
import { isDefined } from '@utils/isDefined';

interface Props {
    patientId: string;
    onCreated(): void;
}

export const SubscriptionCreationPopup = observer(({ patientId, onCreated }: Props): React.ReactElement<any> => {
    const { app, authUser } = store;
    const { createSubscriptionWithoutChat } = useEhrSubscription();

    const [selectedDoctorId, setSelectedDoctorId] = useState<Optional<string>>(
        authUser.isDoctor ? authUser.id : undefined,
    );

    const { billingAmount: defaultBillingAmount, isBillingAmountLoading } = useSubscriptionBillingAmount({
        doctorId: selectedDoctorId,
    });
    const { data: trialDuration, isLoading: isTrialDurationLoading } = useQuery(
        recurringSubscriptionTrialDurationQueryOptions.get(),
    );
    const {
        billingAmount,
        billingAmountDuration,
        isUsedSpecialTerms,
        setBillingAmount,
        setBillingAmountDuration,
        toggleIsUsedSpecialTerms,
    } = useRecurringSubscriptionTerms();

    const { data: practiceDoctors = [] } = useQuery({
        ...userPracticeQueryOptions.doctorsByUser(authUser.id),
        enabled: !authUser.isDoctor,
    });

    const allowedDoctorIds = [...practiceDoctors.map(({ id }) => id), authUser.id];

    const isBillingAmountCorrect = isUsedSpecialTerms
        ? isDefined(billingAmount) && isDefined(billingAmountDuration)
        : isDefined(defaultBillingAmount) && defaultBillingAmount.amount !== 0;

    const isCreationDisabled = !isDefined(selectedDoctorId) || !isBillingAmountCorrect;

    const trialDurationText = isDefined(trialDuration)
        ? `${trialDuration.trialDurationInDays} ${Declension.days(trialDuration.trialDurationInDays)}`
        : translate('limitedTime');
    const costText = defaultBillingAmount
        ? Money.fromKopecks(defaultBillingAmount.amount, defaultBillingAmount.currency).toString()
        : translate('noSubscriptionCost').toLowerCase();

    const closePopup = (): void => app.showPopup(null);

    const onCreate = async (): Promise<void> => {
        assertIsDefined(selectedDoctorId, 'Subscription is created only after a doctor is selected');

        const billingAmountData =
            isUsedSpecialTerms && isDefined(billingAmount) && isDefined(billingAmountDuration)
                ? { billingAmount, billingAmountDuration }
                : undefined;

        const result = await app.doOrShowError({
            action: async () => {
                await createSubscriptionWithoutChat({ patientId, doctorId: selectedDoctorId, billingAmountData });

                return true;
            },
            errorHandler: error => {
                app.showPopup({
                    modifiers: [PopupModifiers.Redesign],
                    description: error?.message ?? translate('error'),
                });
            },
        });

        if (result) {
            onCreated();
            closePopup();
        }
    };

    return (
        <div className={style.popupRoot}>
            <div className={classnames(['font_h1', style.title])}>{translate('addSubscription')}</div>
            {isBillingAmountLoading || isTrialDurationLoading ? (
                <div className={style.preloader}>
                    <Preloader modifiers={[PreloaderModifiers.Small]} />
                </div>
            ) : (
                <>
                    <div className={classnames([style.text, 'font_body16Black'])}>
                        {translate('$freeSubscriptionText', { $cost: costText, $days: trialDurationText })}
                    </div>
                    {!isDefined(defaultBillingAmount) && isDefined(selectedDoctorId) && (
                        <div className={style.text}>
                            <Button
                                dataTest={''}
                                onClick={() => {
                                    window.open(`/user/${selectedDoctorId}?currentPage=subscriptionUserPrice`, '_self');
                                }}
                                theme={ButtonTheme.Link}
                                modifiers={[ButtonModifiers.Small]}
                                label={translate('setSubscriptionCost')}
                            />
                        </div>
                    )}
                </>
            )}
            {!authUser.isDoctor && (
                <div className={style.doctorSelect}>
                    <AsyncUserSelect
                        withAnyOption
                        withFullName
                        title={translate('doctor')}
                        dataTest={''}
                        defaultValue={selectedDoctorId}
                        ids={allowedDoctorIds}
                        onlyEpisodeManagers
                        onChange={option => setSelectedDoctorId(option?.value?.toString() ?? undefined)}
                    />
                </div>
            )}
            <div className={style.text}>
                <RecurringSubscriptionTermsEditor
                    isUsedSpecialTerms={isUsedSpecialTerms}
                    currency={defaultBillingAmount?.currency ?? Currency.RUB}
                    billingAmount={billingAmount}
                    billingAmountDuration={billingAmountDuration}
                    onSpecialTermsChange={toggleIsUsedSpecialTerms}
                    onBillingAmountChange={setBillingAmount}
                    onBillingAmountDurationChange={setBillingAmountDuration}
                />
            </div>
            <div className={style.actionButtons}>
                <Button
                    theme={ButtonTheme.Primary}
                    disabled={isCreationDisabled}
                    label={translate('addSubscription')}
                    onClick={onCreate}
                />
                <Button
                    theme={ButtonTheme.Secondary}
                    label={translate('cancel')}
                    onClick={closePopup}
                />
            </div>
        </div>
    );
});
